import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
// Fonts — all loaded from local @fontsource packages, no render-blocking CDN.
//   Inter Variable → --font-display (headings/titles/stat values). Self-hosted
//     (was a render-blocking Google Fonts CDN <link> in index.html until the
//     2026-07-08 sharpness pass). One variable file, weights 100–900.
//   Geist Mono    → --font-mono (code, HUD lines, numeric readouts).
// Body/UI text uses a Segoe-first system stack (--font-body in globals.css) so
// it renders with DirectWrite ClearType — sharpest possible on Windows — and
// needs no downloaded face. Geist Sans + Plus Jakarta Sans were removed here:
// they were downloaded but referenced by no CSS variable (pure dead weight).
import "@fontsource-variable/inter";
import "@fontsource/geist-mono/400.css";
import "@fontsource/geist-mono/500.css";
// KaTeX styles — required for $math$ rendering in chat (and anywhere else
// react-markdown + rehype-katex shows up).
import "katex/dist/katex.min.css";
import "./styles/globals.css";
import "./stores/theme"; // side-effect: applies saved theme before first render
import "./stores/uiScale"; // side-effect: applies saved UI scale (zoom) before first render
import "./lib/i18n"; // side-effect: initializes i18next with language detection
import { installAuthInterceptor } from "./lib/access";
import { recoverFromChunkFailure } from "./lib/chunkRecovery";
import { clearStaleVaultStateIfChanged } from "./lib/vaultScope";

// Remote access: a same-origin /api answer of 401 shows the sign-in page
// (one wrapper instead of ~400 edits). Idle on the machine itself.
installAuthInterceptor();

// Recover from stale lazy-chunk hashes after a server rebuild: Vite fires
// `vite:preloadError` when a dynamic import() can't find its chunk on disk.
// Wait for the server to answer, then reload — bounded (three reloads in ten
// minutes per tab) and never into an outage. The once-per-session guard that
// lived here stranded a tab whose single reload fell inside a restart window
// (2026-09-10): a white page, a composer saying "reconnecting", nothing left.
window.addEventListener("vite:preloadError", (event) => {
	event.preventDefault();
	void recoverFromChunkFailure();
});

// Per-vault localStorage isolation MUST complete BEFORE React mounts.
// React state initializers run synchronously on the first render and
// read from localStorage (wiki search history, expanded nodes, graph
// scope, command-palette MRU). If we render first and clear async, the
// initializers see stale data from the old vault. The /api/sync probe
// is fast (~10-50ms locally) and bounded by a 1.5s safety timeout so a
// network blip doesn't strand boot.
async function bootstrap() {
	try {
		await Promise.race([
			clearStaleVaultStateIfChanged(),
			new Promise<void>(resolve => setTimeout(resolve, 1500)),
		]);
	} catch {
		// Best-effort — never block mount over an unreachable backend.
	}

	const root = document.getElementById("root");
	if (root) {
		createRoot(root).render(
			<StrictMode>
				<App />
			</StrictMode>,
		);
	}
}

bootstrap();

